import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { TopBar } from '../components/TopBar';
import { Navbar } from '../components/Navbar';
import { useScreenInit } from '../useScreenInit';
import { Calendar, Palette, Package, Truck, CheckCircle2 } from 'lucide-react';
const DETAILED_SERVICES = [{
  title: 'Event Planning',
  subtitle: 'Meticulous Coordination',
  description: 'Custom support for birthdays, showers, weddings, private celebrations, vendor events, and corporate gatherings. We handle the timeline, vendor management, and logistical details so you can enjoy your moment.',
  image: 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?q=80&w=2069&auto=format&fit=crop',
  icon: <Calendar size={24} data-id="element-518" />
}, {
  title: 'Event Design & Styling',
  subtitle: 'Visual Storytelling',
  description: 'Theme development, color coordination, layout planning, décor styling, and visual presentation. Our design team works closely with you to create a cohesive aesthetic that reflects your personality.',
  image: 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?q=80&w=2074&auto=format&fit=crop',
  icon: <Palette size={24} data-id="element-519" />
}, {
  title: 'Event Rentals',
  subtitle: 'Curated Collection',
  description: 'Tables, chairs, linens, backdrops, balloon décor, props, signage, displays, and specialty rental items. Our inventory is meticulously selected to ensure the highest standard of elegance.',
  image: 'https://images.unsplash.com/photo-1478146896981-b80fe463b330?q=80&w=2070&auto=format&fit=crop',
  icon: <Package size={24} data-id="element-520" />
}, {
  title: 'Setup & Breakdown',
  subtitle: 'Seamless Execution',
  description: 'Delivery, setup, styling, and post-event breakdown for a smoother event experience. Our professional team ensures every piece is perfectly placed and carefully removed.',
  image: 'https://images.unsplash.com/photo-1505236858219-8359eb29e329?q=80&w=2162&auto=format&fit=crop',
  icon: <Truck size={24} data-id="element-521" />
}];
const PROCESS_STEPS = [{
  title: 'Inquiry',
  desc: 'Tell us about your event vision and requirements.'
}, {
  title: 'Consultation',
  desc: 'A deep dive into your design preferences and needs.'
}, {
  title: 'Proposal',
  desc: 'A curated selection of services and rentals for your review.'
}, {
  title: 'Execution',
  desc: 'Seamless delivery and styling on your big day.'
}];
export function Service() {
  useScreenInit();
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 0.8
  }} className="min-h-screen bg-white font-sans selection:bg-[#b22222] selection:text-white" data-id="element-522">
      <TopBar data-id="element-523" />
      <Navbar data-id="element-524" />

      <main data-id="element-525">
        {/* Page Hero */}
        <section className="relative h-[40vh] flex items-center justify-center bg-black overflow-hidden" data-id="element-526">
          <div className="absolute inset-0 bg-cover bg-center opacity-50" style={{
          backgroundImage: 'url("https://images.unsplash.com/photo-1513507643553-06196643a29b?q=80&w=2070&auto=format&fit=crop")'
        }} data-id="element-527" />
          <div className="relative text-center text-white z-10" data-id="element-528">
            <motion.span initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="text-[10px] uppercase tracking-[0.4em] font-bold mb-4 block" data-id="element-529">
              Excellence in Every Detail
            </motion.span>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="font-serif text-5xl md:text-7xl italic" data-id="element-530">
              Our Services
            </motion.h1>
          </div>
        </section>

        {/* Detailed Services Breakdown */}
        <section className="py-24" data-id="element-531">
          <div className="mx-4 md:mx-[4.1rem]" data-id="element-532">
            <div className="max-w-[1440px] mx-auto px-6 space-y-32" data-id="element-533">
              {DETAILED_SERVICES.map((service, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 40
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.8
            }} className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-16 items-center`} data-id="element-534">
                  <div className="flex-1 relative" data-id="element-535">
                    <div className="aspect-[16/10] overflow-hidden" data-id="element-536">
                      <img src={service.image} alt={service.title} className="w-full h-full object-cover shadow-2xl" data-id="element-537" />
                    </div>
                    <div className={`absolute -bottom-6 ${index % 2 === 0 ? '-right-6' : '-left-6'} w-24 h-24 bg-[#b22222] text-white flex items-center justify-center shadow-xl`} data-id="element-538">
                      {service.icon}
                    </div>
                  </div>

                  <div className="flex-1 space-y-6" data-id="element-539">
                    <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#b22222]" data-id="element-540">
                      {service.subtitle}
                    </span>
                    <h2 className="font-serif text-4xl text-gray-900" data-id="element-541">
                      {service.title}
                    </h2>
                    <p className="text-gray-600 text-lg leading-relaxed" data-id="element-542">
                      {service.description}
                    </p>
                    <ul className="space-y-3" data-id="element-543">
                      {['Personalized Approach', 'Attention to Detail', 'Professional Team'].map((item, i) => <li key={i} className="flex items-center gap-3 text-sm text-gray-500 font-medium" data-id="element-544">
                          <CheckCircle2 size={16} className="text-[#b22222]" data-id="element-545" />
                          {item}
                        </li>)}
                    </ul>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-24 bg-gray-50" data-id="element-546">
          <div className="mx-4 md:mx-[4.1rem]" data-id="element-547">
            <div className="max-w-[1440px] mx-auto px-6" data-id="element-548">
              <div className="text-center mb-20" data-id="element-549">
                <h2 className="font-serif text-4xl md:text-5xl text-gray-900 mb-6" data-id="element-550">
                  How We Work
                </h2>
                <div className="w-20 h-[1px] bg-[#b22222] mx-auto" data-id="element-551" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12" data-id="element-552">
                {PROCESS_STEPS.map((step, index) => <motion.div key={index} initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: index * 0.1
              }} className="relative text-center space-y-4" data-id="element-553">
                    <div className="text-6xl font-serif italic text-gray-200 absolute -top-10 left-1/2 -translate-x-1/2 z-0" data-id="element-554">
                      0{index + 1}
                    </div>
                    <h3 className="text-xl font-serif text-gray-900 relative z-10" data-id="element-555">
                      {step.title}
                    </h3>
                    <p className="text-gray-500 text-sm leading-relaxed relative z-10" data-id="element-556">
                      {step.desc}
                    </p>
                  </motion.div>)}
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-24 bg-black text-white text-center" data-id="element-557">
          <div className="mx-4 md:mx-[4.1rem]" data-id="element-558">
            <div className="max-w-4xl mx-auto px-6" data-id="element-559">
              <h2 className="font-serif text-4xl md:text-5xl mb-8 italic" data-id="element-560">
                Let's Create Something Beautiful
              </h2>
              <p className="text-gray-400 text-lg mb-12 max-w-2xl mx-auto" data-id="element-561">
                Ready to elevate your next event? Contact us today for a
                personalized consultation and let's bring your vision to life.
              </p>
              <Link to="/contact" data-id="element-562">
                <button className="bg-white text-black px-12 py-5 text-xs font-bold uppercase tracking-widest hover:bg-[#b22222] hover:text-white transition-all duration-300" data-id="element-563">
                  Book a Consultation
                </button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-white py-12 border-t border-gray-100" data-id="element-564">
        <div className="mx-4 md:mx-[4.1rem]" data-id="element-565">
          <div className="max-w-[1440px] mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-8" data-id="element-566">
            <div className="flex flex-col items-center md:items-start leading-none" data-id="element-567">
              <span className="font-script text-3xl text-black" data-id="element-568">
                Chyne Designs
              </span>
              <span className="text-[8px] uppercase tracking-[0.2em] font-bold text-gray-400 mt-1" data-id="element-569">
                Elegant Events & Rentals LLC
              </span>
            </div>
            <div className="flex gap-8 text-[11px] font-bold uppercase tracking-widest text-gray-500" data-id="element-570">
              <a href="#" className="hover:text-black transition-colors" data-id="element-571">
                Privacy Policy
              </a>
              <a href="#" className="hover:text-black transition-colors" data-id="element-572">
                Terms of Service
              </a>
              <a href="#" className="hover:text-black transition-colors" data-id="element-573">
                Contact
              </a>
            </div>
            <p className="text-[11px] text-gray-400" data-id="element-574">
              © 2026 Chyne Designs Elegant Events & Rentals LLC. All rights
              reserved.
            </p>
          </div>
        </div>
      </footer>
    </motion.div>;
}